import { isPast } from 'date-fns'
import { Service } from 'typedi'
import { decodeJWT } from '@/utils'

import {
  setRefreshTokenAction,
  setUserSessionAction,
} from '@/state/auth/slices/index.slices'
import store from '@/state'
import { RequestParams } from './models'

const refreshTokenInterceptor = (response: Response) => {
  const { dispatch } = store

  let jwtRefresh: string | null = ''

  if (response.headers.get('refreshtoken') !== null) {
    jwtRefresh = response.headers.get('refreshtoken')

    if (jwtRefresh) {
      const { isValid, decoded } = decodeJWT(jwtRefresh)
      const hasTokenExpired = () => isPast(new Date(decoded?.exp * 1000))

      if (isValid && !hasTokenExpired()) {
        dispatch(setRefreshTokenAction(jwtRefresh))
        dispatch(setUserSessionAction(decoded))
      }
    }
  }
}

/**
 * @class RestApiService
 * @description This class allows handle REST API calls (PUSH, PUT, GET, DELETE)
 */
@Service()
export class RestApiService {
  private requestParamsProp: RequestParams

  private octToken: string // eslint-disable-line

  constructor() {
    this.octToken =
      process.env && process.env.REACT_APP_TOKEN
        ? process.env.REACT_APP_TOKEN
        : ''
    this.requestParamsProp = {
      method: 'GET',
      type: 'cors',
      endpoint: '',
      queries: '',
      headers: {
        'Content-Type': 'application/json; charset=utf-8;',
        authorization: '',
      },
    }
  }

  set requestParams(requestParams: RequestParams) {
    this.requestParamsProp = requestParams
  }

  get requestParams(): RequestParams {
    return this.requestParamsProp
  }

  /**
   *
   * @param isIncentiveAPI: When the endpoint needs to consume the API from
   * @returns The base server API
   */
  private getServerAPI = (isIncentiveAPI: boolean) => {
    return isIncentiveAPI
      ? process.env && process.env.REACT_APP_SERVER_INCENTIVE_API
      : process.env && process.env.REACT_APP_SERVER_BONUS_ENGINE
  }

  /**
   * @memberof RestApiService
   * @description Build the URL to fetch
   */
  private getUrl = (): string => {
    const { endpoint, queries, isIncentiveAPI, externalServerURL } =
      this.requestParamsProp
    const serverUrl = externalServerURL
      ? externalServerURL
      : this.getServerAPI(isIncentiveAPI)

    return queries
      ? `${serverUrl}${endpoint}?${queries}`
      : `${serverUrl}${endpoint}`
  }

  /**
   * @memberof RestApiService
   * @description Build the URL to authentication service (Brands and User)
   */
  private getAuthUrl = (): string => {
    const { endpoint, queries } = this.requestParamsProp
    const serverUrl = process.env && process.env.REACT_APP_SERVER_MASTER
    return queries
      ? `${serverUrl}${endpoint}?${queries}`
      : `${serverUrl}${endpoint}`
  }

  /**
   * @memberof RestApiService
   * @description Method to perform request over the GET HTTP verb
   */
  getData = async <D>(
    siteID: string,
    token?: string,
    customOctToken?: string,
  ): Promise<D> => {
    const url = this.getUrl()
    try {
      const response = await fetch(url, {
        headers: {
          'Content-Type': 'application/json',
          authorization: `Bearer ${token}`,
          // 'Ocp-Apim-Subscription-Key': customOctToken || this.octToken,
          tenant: siteID,
          'site-id': siteID,
          // 'client-id': 'BONUS_ENGINE_BACKOFFICE',
        },
        method: 'GET',
      })

      refreshTokenInterceptor(response)

      const data = await response.json()
      return data
    } catch (error) {
      return error as any
    }
  }

  /**
   * @memberof RestApiService
   * @description Method to perform request over the GET HTTP verb, authentication service (brand, users)
   */
  getAuthData = async <D>(token: string): Promise<D> => {
    const url = this.getAuthUrl()
    try {
      const response = await fetch(url, {
        headers: {
          'Content-Type': 'application/json',
          authorization: `Bearer ${token}`,
          'Ocp-Apim-Subscription-Key': this.octToken,
          'client-id': 'BONUS_ENGINE_BACKOFFICE',
        },
        method: 'GET',
      })

      refreshTokenInterceptor(response)

      const data = await response.json()
      return data
    } catch (error: any) {
      return error
    }
  }

  /**
   * @memberof RestApiService
   * @description Method to perform request over the POST HTTP verb
   */
  sendData = async <D>(
    siteID: string,
    token?: string,
    customOctToken?: string,
  ): Promise<D> => {
    const { body, method } = this.requestParamsProp
    const url = this.getUrl()
    try {
      const response = await fetch(url, {
        headers: {
          'Content-Type': 'application/json',
          authorization: `Bearer ${token}`,
          // 'Ocp-Apim-Subscription-Key': customOctToken || this.octToken,
          'site-id': siteID,
          // 'client-id': 'BONUS_ENGINE_BACKOFFICE',
        },
        method,
        body: JSON.stringify(body),
      })

      refreshTokenInterceptor(response)

      const data = await response.json()
      return data
    } catch (error: any) {
      return error
    }
  }

  /**
   * @memberof RestApiService
   * @description Method to perform request over the POST HTTP verb, authentication service (brand, users)
   */
  sendAuthData = async <D>(token?: string): Promise<D> => {
    const { body, method } = this.requestParamsProp
    const url = this.getAuthUrl()
    try {
      const response = await fetch(url, {
        headers: {
          'Content-Type': 'application/json',
          authorization: `Bearer ${token}`,
          'Ocp-Apim-Subscription-Key': this.octToken,
          'client-id': 'BONUS_ENGINE_BACKOFFICE',
        },
        method,
        body: JSON.stringify(body),
      })

      refreshTokenInterceptor(response)

      const data = await response.json()
      return data
    } catch (error: any) {
      return error
    }
  }

  /**
   * @memberof RestApiService
   * @description Method to perform request over the PUT HTTP verb, authentication service (brand, users)
   */
  updateAuthData = async <D>(token?: string): Promise<D> => {
    const { body } = this.requestParamsProp
    const url = this.getAuthUrl()
    try {
      const response = await fetch(url, {
        headers: {
          'Content-Type': 'application/json',
          authorization: `Bearer ${token}`,
          'Ocp-Apim-Subscription-Key': this.octToken,
          'client-id': 'BONUS_ENGINE_BACKOFFICE',
        },
        method: 'PUT',
        body: JSON.stringify(body),
      })

      refreshTokenInterceptor(response)

      const data = await response.json()
      return data
    } catch (error: any) {
      return error
    }
  }

  /**
   * @memberof RestApiService
   * @description Method to perform request over the DELETE HTTP verb, authentication service (brand, users)
   */
  deleteAuthData = async <D>(token?: string): Promise<D> => {
    const { body } = this.requestParamsProp
    const url = this.getAuthUrl()
    try {
      const response = await fetch(url, {
        headers: {
          'Content-Type': 'application/json',
          authorization: `Bearer ${token}`,
          'Ocp-Apim-Subscription-Key': this.octToken,
          'client-id': 'BONUS_ENGINE_BACKOFFICE',
        },
        method: 'DELETE',
        body: JSON.stringify(body),
      })

      refreshTokenInterceptor(response)

      const data = await response.json()
      return data
    } catch (error: any) {
      return error
    }
  }
}
